import { createSelector } from '@reduxjs/toolkit';

import { BearingEnvelope } from '@paragon/contract-library';
import { Point2D, point2DEquals, Vector2D } from '@paragon/geometry-library';
import { calculateBearingEnvelopeShape } from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { translateBearingEnvelope } from '../../library/layout-geometry/bearing-envelopes';
import { collectAxisSnapTargetsForInteractiveEdit } from '../../library/layout-snapping/interactive-snapping';
import { RootState } from '../index';
import { BearingEnvelopeMoveEdit, InteractiveEdit } from '../slices/interactiveEditingSlice';
import { isInsertingAnyObject } from './insertionObjectSelectors';
import { getVisibleBearingEnvelopes } from './layoutDataSelectors';
import { getGeometricEcosystemForActivePortionOfLayout } from './layoutGeometricEcosystemSelectors';
import { getActualInteractiveEditSnappingSettings } from './snappingSettingsSelectors';
import { createSimpleSetSelector } from './util/createSimpleSetSelector';

export const getActiveInteractiveEdit = (state: RootState): InteractiveEdit => state.interactiveEditing.activeEdit;

export const shouldInteractiveEditingBeDisabled = (state: RootState): boolean =>
  isInsertingAnyObject(state) || state.popup.popupName != null;

/**
 * Returns the type of the active interactive edit, or null if no such edit is active.
 */
export const getActiveEditType = createSelector(getActiveInteractiveEdit, (activeEdit) => activeEdit?.type ?? null);

/**
 * Returns whether an interactive edit is currently active.
 */
export const isInteractiveEditActive = createSelector(getActiveInteractiveEdit, (activeEdit) => activeEdit !== null);

export const getActiveEditHasMoved = createSelector(getActiveInteractiveEdit, (activeEdit): boolean => {
  if (!activeEdit) return false;

  switch (activeEdit.type) {
    case 'bearingEnvelopeLength': {
      const { baseEnvelope, currentPatch } = activeEdit;
      const leftMoved =
        currentPatch.leftPoint != null && !point2DEquals(baseEnvelope.leftPoint, currentPatch.leftPoint);
      const rightMoved =
        currentPatch.rightPoint != null && !point2DEquals(baseEnvelope.rightPoint, currentPatch.rightPoint);

      return leftMoved || rightMoved;
    }

    case 'bearingEnvelopeMove':
      return !point2DEquals(Point2D.origin, activeEdit.currentDelta);

    default:
      assertExhaustiveSwitch(activeEdit);
  }
});

const getActiveLengthEditGuid = (state: RootState): string | null => {
  const activeEdit = getActiveInteractiveEdit(state);
  return activeEdit?.type === 'bearingEnvelopeLength' ? activeEdit.guid : null;
};

const EMPTY_EDITED_ENVELOPES: BearingEnvelopeMoveEdit['editedEnvelopes'] = [];
const EMPTY_POINTS: readonly Point2D[] = [];

const getActiveMoveEditEditedEnvelopes = (state: RootState): BearingEnvelopeMoveEdit['editedEnvelopes'] => {
  const activeEdit = getActiveInteractiveEdit(state);
  return activeEdit?.type === 'bearingEnvelopeMove' ? activeEdit.editedEnvelopes : EMPTY_EDITED_ENVELOPES;
};

export const getActiveMoveEditBaseShapeVertices = createSelector(
  getActiveMoveEditEditedEnvelopes,
  (editedEnvelopes): readonly Point2D[] => {
    if (editedEnvelopes.length === 0) return EMPTY_POINTS;

    const vertices: Point2D[] = [];
    for (const entry of editedEnvelopes) {
      const shape = calculateBearingEnvelopeShape(entry.baseEnvelope);
      if (shape) vertices.push(...shape.vertices);
    }

    return vertices;
  },
);

/**
 * Returns the guids of all bearing envelopes being edited during an active interactive edit.
 */
export const getEditingBearingEnvelopeGuids = createSimpleSetSelector(
  getActiveEditType,
  getActiveLengthEditGuid,
  getActiveMoveEditEditedEnvelopes,
  (activeEditType, activeLengthEditGuid, editedEnvelopes): ReadonlySet<string> => {
    switch (activeEditType) {
      case null:
        return new Set();

      case 'bearingEnvelopeLength':
        return activeLengthEditGuid == null ? new Set() : new Set([activeLengthEditGuid]);

      case 'bearingEnvelopeMove': {
        return new Set(editedEnvelopes.map((envelope) => envelope.guid));
      }

      default:
        assertExhaustiveSwitch(activeEditType);
    }
  },
);

export const getInteractiveEditAxisSnapTargets = createSelector(
  getGeometricEcosystemForActivePortionOfLayout,
  getActualInteractiveEditSnappingSettings,
  getEditingBearingEnvelopeGuids,

  (ecosystem, settings, editingGuids) => collectAxisSnapTargetsForInteractiveEdit(ecosystem, settings, editingGuids),
);

/**
 * Returns visible bearing envelopes with the active interactive edits applied.
 */
export const getVisibleBearingEnvelopesWithActiveEdit = createSelector(
  getVisibleBearingEnvelopes,
  getActiveInteractiveEdit,
  (envelopes, activeEdit): readonly BearingEnvelope[] => {
    if (!activeEdit) return envelopes;

    if (activeEdit.type === 'bearingEnvelopeMove') {
      const delta = Vector2D.fromOriginTo(activeEdit.currentDelta);
      const editedMap = new Map(activeEdit.editedEnvelopes.map((e) => [e.guid, e.baseEnvelope]));

      return envelopes.map((envelope) => {
        const editedBase = editedMap.get(envelope.guid);
        if (editedBase) return translateBearingEnvelope(editedBase, delta);

        return envelope;
      });
    }

    return envelopes.map((envelope) =>
      envelope.guid === activeEdit.guid ? { ...activeEdit.baseEnvelope, ...activeEdit.currentPatch } : envelope,
    );
  },
);
